import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { StatCard } from "../components/common/StatCard";
import { PageHeader } from "../components/common/PageHeader";
import { useDataFetching } from "../hooks/useDataFetching";
import { useScopeFilter } from "../hooks/useScopeFilter";
import {
  usersAPI,
  eventsAPI,
  ideasAPI,
  schoolsAPI,
  appDownloadsAPI,
} from "../api";
import { QUERY_KEYS } from "../utils/constants";
import { formatNumber, formatDate } from "../utils/formatters";
import {
  Users,
  Calendar,
  Lightbulb,
  GraduationCap,
  Download,
} from "lucide-react";
import { User, Event, Idea, AppDownloadStats, ApiDataResponse } from "../types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

export function DashboardPage() {
  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
  } = useDataFetching(QUERY_KEYS.USERS, {
    queryFn: usersAPI.getAllUsers,
  });

  const {
    data: eventsData,
    isLoading: eventsLoading,
    error: eventsError,
  } = useDataFetching(QUERY_KEYS.EVENTS, {
    queryFn: eventsAPI.getAllEvents,
  });

  const {
    data: ideasData,
    isLoading: ideasLoading,
    error: ideasError,
  } = useDataFetching(QUERY_KEYS.IDEAS, {
    queryFn: ideasAPI.getAllIdeas,
  });

  const {
    data: schoolsData,
    isLoading: schoolsLoading,
    error: schoolsError,
  } = useDataFetching(QUERY_KEYS.SCHOOLS, {
    queryFn: schoolsAPI.getAllSchools,
  });

  const { data: appDownloadsData, isLoading: appDownloadsLoading } =
    useDataFetching(QUERY_KEYS.APP_DOWNLOADS, {
      queryFn: appDownloadsAPI.getDownloadStats,
    });

  const { filterByScope, schoolId } = useScopeFilter();

  const users = filterByScope(Array.isArray(usersData) ? usersData : []);
  const events = filterByScope(
    Array.isArray(eventsData)
      ? eventsData
      : (eventsData as ApiDataResponse<Event[]>)?.data || []
  );
  const ideas = filterByScope(
    Array.isArray(ideasData)
      ? ideasData
      : (ideasData as ApiDataResponse<Idea[]>)?.data || []
  );
  const allSchools = Array.isArray(schoolsData) ? schoolsData : [];
  const schools = schoolId
    ? allSchools.filter((s) => s._id === schoolId)
    : allSchools;

  const sortedUsers = [...users].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const isLoading =
    usersLoading ||
    eventsLoading ||
    ideasLoading ||
    schoolsLoading ||
    appDownloadsLoading;

  const hasError = usersError || eventsError || ideasError || schoolsError;

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState type="dashboard" />
      </DashboardLayout>
    );
  }

  if (hasError) {
    return (
      <DashboardLayout>
        <ErrorState
          title="Error loading dashboard data"
          message="Please try refreshing the page. You can also logout from your profile menu in the sidebar."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="responsive-container">
        <div className="space-y-6">
          <PageHeader
            title="Dashboard"
            description="Welcome to the Walky Admin Dashboard"
          />

          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard
              title="Total Users"
              value={formatNumber(users.length)}
              description="Registered users"
              icon={<Users className="h-4 w-4" />}
              variant="blue"
            />

            <StatCard
              title="Total Events"
              value={formatNumber(events.length)}
              description="Events created"
              icon={<Calendar className="h-4 w-4" />}
              variant="purple"
            />

            <StatCard
              title="Total Ideas"
              value={formatNumber(ideas.length)}
              description="Ideas shared"
              icon={<Lightbulb className="h-4 w-4" />}
              variant="amber"
            />

            <StatCard
              title="Total Schools"
              value={formatNumber(schools.length)}
              description="Schools registered"
              icon={<GraduationCap className="h-4 w-4" />}
              variant="cyan"
            />

            <StatCard
              title="App Downloads"
              value={formatNumber(
                appDownloadsData
                  ? (appDownloadsData as AppDownloadStats).totalDownloads || 0
                  : 0
              )}
              description="Total downloads"
              icon={<Download className="h-4 w-4" />}
              variant="purple"
            />
          </div>

          <div className="grid gap-6 grid-cols-1">
            <Card>
              <CardHeader>
                <CardTitle>Recent Users</CardTitle>
                <CardDescription>
                  {isLoading
                    ? "Loading..."
                    : `${sortedUsers.length} users registered`}
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-auto max-h-[400px]">
                {isLoading ? (
                  <div className="text-center py-4">Loading...</div>
                ) : sortedUsers.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    No users found
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sortedUsers.slice(0, 5).map((user: User) => (
                      <div key={user._id} className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          {user.first_name?.charAt(0) || ""}
                          {user.last_name?.charAt(0) || ""}
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-sm font-medium leading-none">
                            {user.first_name || ""} {user.last_name || ""}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(user.createdAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
